"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ManualNote = exports.ManualSubheading = exports.ManualSection = exports.ManualLayout = exports.scrollToManualSection = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * RedPanda UI - 使用手冊版面（docs/介面優化.md §3-18、R26）
 * - ManualLayout：頁標題＋左側固定目錄（不包卡片、輔助色連結）＋右側內容
 *   目錄會標示目前讀到的章節；點目錄時網址帶上 #章節，連結可以直接分享到該章
 * - ManualSection：章節（區塊標題 text-lg font-semibold，不再和頁標題一樣大）
 * - ManualSubheading：章節內小標
 * - ManualNote：提示框（中性淡底，不用彩色色塊、不放 emoji）
 * 各系統的手冊內容（章節、翻譯）不同，只共用版面與字級
 */
const react_1 = require("react");
const utils_1 = require("../../utils");
/** 捲到章節（留 30px 讓標題不貼齊視窗頂端） */
const scrollToManualSection = (id) => {
    const element = document.getElementById(id);
    if (element)
        window.scrollTo({ top: element.offsetTop - 30, behavior: 'auto' });
};
exports.scrollToManualSection = scrollToManualSection;
/** 目前讀到的章節：最後一個標題已捲過視窗上方 1/4 的章節（捲到底時是最後一章） */
const useActiveSection = (ids) => {
    const [active, setActive] = (0, react_1.useState)(ids[0] ?? null);
    const key = ids.join(',');
    (0, react_1.useEffect)(() => {
        const update = () => {
            const line = window.innerHeight / 4;
            const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
            let current = ids[0] ?? null;
            for (const id of ids) {
                const el = document.getElementById(id);
                if (el && el.getBoundingClientRect().top <= line)
                    current = id;
            }
            setActive(atBottom ? ids[ids.length - 1] ?? current : current);
        };
        update();
        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);
    return active;
};
const ManualLayout = ({ title, tocTitle, toc, children }) => {
    const active = useActiveSection(toc.map(item => item.id));
    const go = (id) => {
        (0, exports.scrollToManualSection)(id);
        window.history.replaceState(null, '', `#${id}`); // 不新增瀏覽記錄，上一頁仍回到進手冊前的頁面
    };
    return ((0, jsx_runtime_1.jsxs)("div", { className: "container mx-auto px-10 py-6 pb-32", children: [(0, jsx_runtime_1.jsx)("div", { className: "mb-8", children: (0, jsx_runtime_1.jsx)("h1", { className: "text-2xl font-semibold text-gray-900 dark:text-white", children: title }) }), (0, jsx_runtime_1.jsxs)("div", { className: "grid grid-cols-1 lg:grid-cols-[11rem_1fr] gap-8", children: [(0, jsx_runtime_1.jsx)("div", { children: (0, jsx_runtime_1.jsx)("div", { className: "sticky top-8", children: (0, jsx_runtime_1.jsxs)("nav", { className: "text-sm", children: [(0, jsx_runtime_1.jsx)("h2", { className: "font-semibold text-fg mb-3", children: tocTitle }), (0, jsx_runtime_1.jsx)("ul", { className: "space-y-1.5 border-l border-line", children: toc.map(item => ((0, jsx_runtime_1.jsx)("li", { children: (0, jsx_runtime_1.jsx)("button", { type: "button", onClick: () => go(item.id), "aria-current": active === item.id ? 'location' : undefined, className: (0, utils_1.cn)('text-left w-full pl-3 -ml-px border-l transition-colors', active === item.id ? 'border-accent text-accent font-medium' : 'border-transparent text-fg-secondary hover:text-fg hover:border-fg'), children: item.label }) }, item.id))) })] }) }) }), (0, jsx_runtime_1.jsx)("div", { className: "min-w-0 max-w-3xl", children: children })] })] }));
};
exports.ManualLayout = ManualLayout;
exports.ManualLayout.displayName = 'ManualLayout';
const ManualSection = ({ id, title, children }) => ((0, jsx_runtime_1.jsxs)("section", { id: id, className: "mb-12", children: [(0, jsx_runtime_1.jsx)("h2", { className: "text-lg font-semibold text-fg mb-4", children: title }), (0, jsx_runtime_1.jsx)("div", { className: "text-sm text-fg-secondary leading-relaxed space-y-4", children: children })] }));
exports.ManualSection = ManualSection;
exports.ManualSection.displayName = 'ManualSection';
const ManualSubheading = ({ children, className }) => ((0, jsx_runtime_1.jsx)("h3", { className: (0, utils_1.cn)('text-base font-semibold text-fg mt-6', className), children: children }));
exports.ManualSubheading = ManualSubheading;
exports.ManualSubheading.displayName = 'ManualSubheading';
const ManualNote = ({ title, children }) => ((0, jsx_runtime_1.jsxs)("div", { className: "p-4 bg-slate-50 dark:bg-slate-800/60 rounded-lg border border-line", children: [title && (0, jsx_runtime_1.jsx)("p", { className: "font-semibold text-fg", children: title }), (0, jsx_runtime_1.jsx)("div", { className: (0, utils_1.cn)('text-fg-secondary', title && 'mt-1'), children: children })] }));
exports.ManualNote = ManualNote;
exports.ManualNote.displayName = 'ManualNote';
